import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button, Card, StatusPill } from '../design-system';
import '../styles/StyleGuide.css';

const NEUTRAL_SWATCHES = [
  ['0', '#ffffff'], ['25', '#fcfbfa'], ['50', '#f6f5f3'], ['100', '#f1efeb'],
  ['200', '#e9e7e3'], ['300', '#d8d5d0'], ['400', '#a5a19a'], ['500', '#8a857e'],
  ['600', '#6e6b66'], ['700', '#4a4843'], ['800', '#2e2c28'], ['900', '#1c1b19'], ['950', '#100f0e'],
];

const ACCENT_SWATCHES = [
  ['50', '#eaf0ec'], ['100', '#dde7e0'], ['200', '#c3d2c8'], ['300', '#9db3a4'],
  ['400', '#77947f'], ['500', '#5e7d69'], ['600', '#4f6f5c'], ['700', '#435f4f'],
  ['800', '#37503f'], ['900', '#2b3e32'],
];

const SEMANTIC_SWATCHES = [
  { name: 'Success', hex: '#4f6f5c', bg: '#eaf0ec' },
  { name: 'Warning', hex: '#b08428', bg: '#f8f1de' },
  { name: 'Danger', hex: '#9c5b3e', bg: '#fbf1ed' },
  { name: 'Info', hex: '#2b3a4a', bg: '#ebeef1' },
];

const TYPE_SCALE = [
  { name: 'Display', className: 'ds-preview-display', spec: '700 / 48px / 1.1' },
  { name: 'H1', className: 'ds-preview-h1', spec: '700 / 36px / 1.2' },
  { name: 'H2', className: 'ds-preview-h2', spec: '700 / 30px / 1.25' },
  { name: 'H3', className: 'ds-preview-h3', spec: '600 / 24px / 1.3' },
  { name: 'H4', className: 'ds-preview-h4', spec: '600 / 20px / 1.4' },
  { name: 'Body Large', className: 'ds-preview-body-lg', spec: '400 / 18px / 1.6' },
  { name: 'Body', className: 'ds-preview-body', spec: '400 / 16px / 1.6' },
  { name: 'Body Small', className: 'ds-preview-body-sm', spec: '400 / 14px / 1.5' },
  { name: 'Caption', className: 'ds-preview-caption', spec: '500 / 12px / 1.4' },
];

const DEMO_STATUSES = [
  'todo', 'in_progress', 'in_review', 'changes_requested', 'done',
  'upcoming', 'overdue', 'filed', 'late_filed', 'not_applicable',
  'active', 'dormant', 'onboarding', 'proposed', 'approved', 'rejected',
];

const CYCLE_STATUSES = ['todo', 'in_progress', 'in_review', 'done'];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] } },
};

function Section({ title, description, children }) {
  return (
    <section className="ds-section">
      <div className="ds-section-heading">
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {children}
    </section>
  );
}

function StyleGuide() {
  const [loading, setLoading] = useState(false);
  const [cycleIndex, setCycleIndex] = useState(0);

  const handleLoadingDemo = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1600);
  };

  return (
    <div className="ds-root style-guide-page">
      <header className="style-guide-header">
        <span className="style-guide-eyebrow">Design Language · v0.1</span>
        <h1>Style Guide</h1>
        <p>
          Palette, type, and the first three primitives — Button, Card, and StatusPill — proposed as
          the foundation for the rest of the product. Nothing on existing screens changes until this
          is signed off.
        </p>
      </header>

      <Section
        title="Color"
        description="One refined accent, a generous cool-neutral scale, and four semantic colors for status/success/warning/danger."
      >
        <h3 className="ds-subhead">Accent — Indigo</h3>
        <div className="swatch-row">
          {ACCENT_SWATCHES.map(([step, hex]) => (
            <div key={step} className="swatch" style={{ background: hex }}>
              <span className="swatch-label" style={{ color: step > 400 ? '#fff' : '#1c1b19' }}>
                {step}
              </span>
            </div>
          ))}
        </div>

        <h3 className="ds-subhead">Neutrals</h3>
        <div className="swatch-row">
          {NEUTRAL_SWATCHES.map(([step, hex]) => (
            <div
              key={step}
              className="swatch"
              style={{ background: hex, border: step < 100 ? '1px solid #e9e7e3' : 'none' }}
            >
              <span className="swatch-label" style={{ color: step > 400 ? '#fff' : '#1c1b19' }}>
                {step}
              </span>
            </div>
          ))}
        </div>

        <h3 className="ds-subhead">Semantic</h3>
        <div className="semantic-row">
          {SEMANTIC_SWATCHES.map((s) => (
            <div key={s.name} className="semantic-swatch" style={{ background: s.bg }}>
              <div className="semantic-dot" style={{ background: s.hex }} />
              <div>
                <div className="semantic-name">{s.name}</div>
                <div className="semantic-hex">{s.hex}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Typography" description="Inter, with a restrained nine-step scale.">
        <div className="type-scale">
          {TYPE_SCALE.map((t) => (
            <div key={t.name} className="type-row">
              <div className="type-meta">
                <span className="type-name">{t.name}</span>
                <span className="type-spec">{t.spec}</span>
              </div>
              <div className={t.className}>The quick brown fox files a return</div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Button"
        description="Four variants, three sizes. Subtle lift on hover, a quick press-down on tap, both skipped entirely under prefers-reduced-motion."
      >
        <div className="demo-block">
          <div className="demo-row">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
          </div>
          <div className="demo-row">
            <Button variant="primary" size="sm">Small</Button>
            <Button variant="primary" size="md">Medium</Button>
            <Button variant="primary" size="lg">Large</Button>
          </div>
          <div className="demo-row">
            <Button variant="primary" disabled>Disabled</Button>
            <Button variant="primary" loading={loading} onClick={handleLoadingDemo}>
              {loading ? 'Saving…' : 'Click to load'}
            </Button>
          </div>
        </div>
      </Section>

      <Section
        title="Card"
        description="A quiet surface with a soft shadow and a 1px border. Interactive cards lift on hover; the grid below staggers in on mount."
      >
        <motion.div
          className="card-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {[
            { name: 'Sharma Textiles', meta: 'Private Ltd · GST + TDS', status: 'active' },
            { name: 'Kapoor & Associates', meta: 'Partnership · ITR due in 12 days', status: 'in_progress' },
            { name: 'Verma Exports', meta: 'LLP · Audit overdue', status: 'overdue' },
          ].map((c) => (
            <Card key={c.name} interactive variants={itemVariants}>
              <Card.Header title={c.name} subtitle={c.meta} />
              <StatusPill status={c.status} />
            </Card>
          ))}
        </motion.div>
      </Section>

      <Section
        title="StatusPill"
        description="Every status string used across the app maps to one of five semantic colors. Unknown statuses fall back to neutral instead of breaking."
      >
        <div className="pill-grid">
          {DEMO_STATUSES.map((s) => (
            <StatusPill key={s} status={s} />
          ))}
        </div>

        <div className="demo-row" style={{ marginTop: 'var(--ds-space-6)' }}>
          <StatusPill status={CYCLE_STATUSES[cycleIndex]} />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCycleIndex((i) => (i + 1) % CYCLE_STATUSES.length)}
          >
            Advance status
          </Button>
        </div>
      </Section>
    </div>
  );
}

export default StyleGuide;
